import { APIShortGameType, type PlatformType, ShortGameType, ShortGameZipType } from '@/types'

export function getCurrentPlatform(userAgent: string): PlatformType {
    if (userAgent.match(/(iPhone|iPad|iPod)/)) {
        return 'ios'
    }
    if (userAgent.match(/Android/)) {
        return 'android'
    }
    return 'desktop'
}

// TODO change to overriding directly Math.random once https://github.com/mswjs/msw/pull/1617 is ready.
// that allows to run node server in a separate process (now it is part of playwright process) and mock anything there

// eslint-disable-next-line no-restricted-syntax
export const random = typeof E2ERandomMock !== 'undefined' ? E2ERandomMock : Math.random

export function shuffle<T>(array: T[]): T[] {
    const result = [...array]

    for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(random() * (i + 1))
        ;[result[i], result[j]] = [result[j]!, result[i]!]
    }

    return result
}

export function timer<T = void>(
    timeoutMs: number,
    {
        abortSignal,
        value,
    }: {
        abortSignal?: AbortSignal
        value?: T
    } = {},
): Promise<T> {
    return new Promise((res) => {
        const timeoutId = setTimeout(() => {
            res(value as T)
        }, timeoutMs)

        abortSignal?.addEventListener('abort', () => {
            clearTimeout(timeoutId)
        })
    })
}

export function zipAPIGame(game: APIShortGameType): ShortGameZipType {
    return [
        game.hru,
        game.title,
        game.preview,
        game.icon,
        game.video_metadata[0]?.external_url,
        game.video_metadata[0]?.playgama_id,
        game.hasInAppPurchases,
        game.rankingDebug?.data,
        game.badges,
        game.source,
        game.platforms,
    ]
}

export function unzipShortGame([
    hru,
    title,
    preview,
    icon,
    videoUrl,
    videoId,
    hasInAppPurchases,
    debugInfo,
    badges,
    source,
    platforms,
]: ShortGameZipType): ShortGameType {
    return { hru, title, preview, icon, videoUrl, videoId, hasInAppPurchases, debugInfo, badges, source, platforms }
}

export function idleCallbackPromise(deadline?: number): Promise<void> {
    return new Promise((resolve) => {
        if (window.requestIdleCallback) {
            window.requestIdleCallback(
                () => {
                    resolve()
                },
                { timeout: deadline },
            )
        } else {
            setTimeout(() => {
                resolve()
            }, 1)
        }
    })
}

export function getYoutubeId(value: string | undefined) {
    if (value?.includes('https://www.youtu.be/')) {
        return value.split('https://www.youtu.be/')[1]?.split('?')[0]
    }

    if (value?.includes('https://www.youtube.com/watch?v=')) {
        return value.split('https://www.youtube.com/watch?v=')[1]?.split('&')[0]
    }

    if (value?.includes('https://www.youtube.com/embed/')) {
        return value.split('https://www.youtube.com/embed/')[1]?.split('?')[0]
    }

    return ''
}

export function getYoutubeEmbedLink(value: string | undefined) {
    const youTubeLink = getYoutubeId(value)
    if (!youTubeLink) {
        return undefined
    }

    return `https://www.youtube.com/embed/${youTubeLink}`
}

/*  according to ISO week date rules (ISO 8601):
    https://en.wikipedia.org/wiki/ISO_week_date

    Week 01 is the week that contains 4 January (equivalently: the week with Thursday).
    The ISO "week-year" is the Gregorian year of that Thursday;
    Algorithm:
        1) Shift the given date to the Thursday of its week.
        2) Find the Thursday of ISO week 1: take 4 Jan of that ISO year and shift to its Thursday.
        3) The week number is 1 + the whole number of weeks between those two Thursdays.
*/
export function getUTCYearWeek(dateInMsec: number): string {
    const date = new Date(dateInMsec)

    const dateUTC = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
    // shift dayNum to have 0 = Monday
    const dayNum = (dateUTC.getUTCDay() + 6) % 7

    dateUTC.setUTCDate(dateUTC.getUTCDate() - dayNum + 3)
    const isoYear = dateUTC.getUTCFullYear()
    const firstThursday = new Date(Date.UTC(isoYear, 0, 4))
    const firstDayNum = (firstThursday.getUTCDay() + 6) % 7
    firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNum + 3)

    const isoWeek = 1 + Math.round((dateUTC.getTime() - firstThursday.getTime()) / (7 * 24 * 3600 * 1000))
    return `${isoYear}_${String(isoWeek).padStart(2, '0')}`
}

export function isIframe() {
    return window.self !== window.top
}
